/**
 * Object Mutation Hooks
 * React Query mutations for creating, updating, and managing objects
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query/keys'
import { useSession } from '@/features/auth/hooks/useSession'
import type {
  CreateObjectRequest,
  UpdateObjectRequest,
} from '@/lib/api/endpoints/objects'

/**
 * Create a new object (task, event, contact, etc.)
 */
export function useCreateObject() {
  const { token } = useSession()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateObjectRequest) =>
      api.objects.createObject(token, data),
    onSuccess: (result, variables) => {
      // Invalidate all object lists
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.lists() })

      // If created with a specific type, invalidate that type's list
      if (variables.type) {
        queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType(variables.type) })
      }
    },
  })
}

/**
 * Update an existing object
 * Takes objectType, objectId, data, and optional etag in the mutation call
 * If etag is not provided, uses version number as fallback
 */
export function useUpdateObject() {
  const { token } = useSession()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ objectType, objectId, data, etag }: {
      objectType: string
      objectId: string
      data: UpdateObjectRequest
      etag?: string
    }) =>
      api.objects.updateObject(token, objectType, objectId, data, etag),
    onSuccess: (_, variables) => {
      // Invalidate the specific object
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.detail(variables.objectId) })

      // Invalidate all object lists to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.lists() })

      // Invalidate by type to refresh the contacts/tasks/etc list
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType(variables.objectType) })
    },
  })
}

/**
 * Retry the failed outbound calendar push for a single event (#3951).
 * Re-runs the push server-side and returns the re-stamped event; invalidates the
 * event lists/detail so the "not synced" badge reflects the new providerSyncState.
 */
export function useRetryEventSync() {
  const { token } = useSession()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ eventId }: { eventId: string }) =>
      api.objects.retryEventSync(token, eventId),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.detail(variables.eventId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('event') })
    },
  })
}

/**
 * Delete an object (task, event, contact, etc.). Mirror of useCreateObject.
 */
export function useDeleteObject() {
  const { token } = useSession()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ objectType, objectId }: { objectType: string; objectId: string }) =>
      api.objects.deleteObject(token, objectType, objectId),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.detail(variables.objectId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType(variables.objectType) })
    },
  })
}

